import axios from 'axios';

const GEOLOCATION_API = 'https://validationapi.thetechmenders.com/api/GeoLocation/GetGeoLocation';
const LEAD_API = 'https://leadapi.crmbytes.com/api/Lead/AddLead';

// Geolocation Service
export const getGeoLocation = async () => {
  try {
    const response = await axios.get(GEOLOCATION_API, {
      headers: {
        'accept': 'text/plain'
      }
    });
    
    if (response.data.isSuccess) {
      return {
        success: true,
        data: response.data.data
      };
    }
    
    return {
      success: false,
      error: 'Failed to fetch geolocation'
    };
  } catch (error) {
    console.error('Geolocation API Error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Lead Submission Service
export const submitLead = async (leadData) => {
  try {
    // Get geolocation data
    const geoResult = await getGeoLocation();
    let geoLocationData = null;
    let ipAddress = '';
    let city = '';
    let state = '';
    let country = '';
    let countryDialCode = leadData.countryDialCode || '';

    if (geoResult.success) {
      geoLocationData = geoResult.data;
      ipAddress = geoLocationData.ipAddress || '';
      city = geoLocationData.cityName || '';
      state = geoLocationData.regionName || '';
      country = geoLocationData.countryCode || leadData.country || '';
      
      // Try to extract dial code from country codes map
      if (!countryDialCode && country) {
        countryDialCode = getDialCodeFromCountry(country);
      }
    }

    // Clean phone number - remove all non-numeric characters
    const cleanPhoneNumber = (leadData.phoneNumber || '').replace(/[^\d]/g, '');

    // Prepare payload
    const payload = {
      name: leadData.name || '',
      email: leadData.email || '',
      countryDialCode: parseInt(countryDialCode) || 1,
      country: country || 'US',
      phoneNumber: cleanPhoneNumber,
      ipAddress: ipAddress || '0.0.0.0',
      description: leadData.message || leadData.description || '',
      pageUrl: window.location.href || '',
      city: city || '',
      state: state || '',
      geoLocation: geoLocationData ? JSON.stringify({ 
        isSuccess: true, 
        data: geoLocationData, 
        errorMessage: '', 
        statusCode: 200 
      }) : '',
      packageTitle: leadData.packageTitle || '',
      packageAmount: leadData.packageAmount || 0
    };

    console.log('Submitting lead with payload:', payload);

    const response = await axios.post(LEAD_API, payload, {
      headers: {
        'Content-Type': 'application/json',
        'Origin': window.location.origin,
        'Referer': window.location.href
      },
      withCredentials: false
    });

    console.log('Lead API Response:', response.data);

    // Handle successful response (200)
    if (response.data.statusCode === 200 && response.data.isSuccess) {
      console.log('Lead submitted successfully! CustomerId:', response.data.data.customerId);
      return {
        success: true,
        customerId: response.data.data.customerId,
        message: response.data.data.message,
        data: response.data.data
      };
    }

    // Handle other success codes
    console.log('Lead submission returned non-200 status:', response.data.statusCode);
    return {
      success: false,
      error: response.data.errorMessage || 'Unknown error occurred',
      statusCode: response.data.statusCode
    };

  } catch (error) {
    console.error('Lead Submission Error:', error);
    
    // Handle 401 Unauthorized
    if (error.response && error.response.status === 401) {
      return {
        success: false,
        error: error.response.data.message || 'Unauthorized',
        statusCode: 401
      };
    }
    
    // Handle 400 Bad Request
    if (error.response && error.response.status === 400) {
      return {
        success: false,
        error: error.response.data.errorMessage || error.response.data.data?.message || 'Bad Request',
        statusCode: 400
      };
    }
    
    // Handle network or other errors
    return {
      success: false,
      error: error.message || 'Network error occurred',
      statusCode: error.response?.status || 500
    };
  }
};

// Helper function to get dial code from country code
const getDialCodeFromCountry = (countryCode) => {
  const dialCodes = {
    'US': 1, 'CA': 1, 'GB': 44, 'AU': 61, 'NZ': 64, 'IN': 91, 'PK': 92,
    'AF': 93, 'AL': 355, 'DZ': 213, 'AD': 376, 'AO': 244, 'AR': 54,
    'AM': 374, 'AT': 43, 'AZ': 994, 'BH': 973, 'BD': 880, 'BY': 375,
    'BE': 32, 'BZ': 501, 'BJ': 229, 'BT': 975, 'BO': 591, 'BA': 387,
    'BW': 267, 'BR': 55, 'BN': 673, 'BG': 359, 'BF': 226, 'BI': 257,
    'KH': 855, 'CM': 237, 'CV': 238, 'CF': 236, 'TD': 235, 'CL': 56,
    'CN': 86, 'CO': 57, 'KM': 269, 'CG': 242, 'CR': 506, 'HR': 385,
    'CU': 53, 'CY': 357, 'CZ': 420, 'DK': 45, 'DJ': 253, 'EC': 593,
    'EG': 20, 'SV': 503, 'GQ': 240, 'ER': 291, 'EE': 372, 'ET': 251,
    'FJ': 679, 'FI': 358, 'FR': 33, 'GA': 241, 'GM': 220, 'GE': 995,
    'DE': 49, 'GH': 233, 'GR': 30, 'GT': 502, 'GN': 224, 'GW': 245,
    'GY': 592, 'HT': 509, 'HN': 504, 'HK': 852, 'HU': 36, 'IS': 354,
    'ID': 62, 'IR': 98, 'IQ': 964, 'IE': 353, 'IL': 972, 'IT': 39,
    'CI': 225, 'JP': 81, 'JO': 962, 'KZ': 7, 'KE': 254, 'KW': 965,
    'KG': 996, 'LA': 856, 'LV': 371, 'LB': 961, 'LS': 266, 'LR': 231,
    'LY': 218, 'LI': 423, 'LT': 370, 'LU': 352, 'MO': 853, 'MK': 389,
    'MG': 261, 'MW': 265, 'MY': 60, 'MV': 960, 'ML': 223, 'MT': 356,
    'MR': 222, 'MU': 230, 'MX': 52, 'MD': 373, 'MC': 377, 'MN': 976,
    'ME': 382, 'MA': 212, 'MZ': 258, 'MM': 95, 'NA': 264, 'NP': 977,
    'NL': 31, 'NI': 505, 'NE': 227, 'NG': 234, 'NO': 47, 'OM': 968,
    'PL': 48, 'PT': 351, 'QA': 974, 'RO': 40, 'RU': 7, 'RW': 250,
    'SA': 966, 'SN': 221, 'RS': 381, 'SC': 248, 'SL': 232, 'SG': 65,
    'SK': 421, 'SI': 386, 'SO': 252, 'ZA': 27, 'KR': 82, 'SS': 211,
    'ES': 34, 'LK': 94, 'SD': 249, 'SR': 597, 'SZ': 268, 'SE': 46,
    'CH': 41, 'SY': 963, 'TW': 886, 'TJ': 992, 'TZ': 255, 'TH': 66,
    'TG': 228, 'TN': 216, 'TR': 90, 'TM': 993, 'UG': 256, 'UA': 380,
    'AE': 971, 'UY': 598, 'UZ': 998, 'VE': 58, 'VN': 84, 'YE': 967,
    'ZM': 260, 'ZW': 263
  };
  
  return dialCodes[countryCode] || 1;
};

// Function to get country name from country code
export const getCountryName = (countryCode) => {
  const countries = {
    'US': 'USA/Canada', 'CA': 'Canada', 'GB': 'United Kingdom', 'AU': 'Australia',
    'NZ': 'New Zealand', 'IN': 'India', 'PK': 'Pakistan', 'AF': 'Afghanistan',
    'AL': 'Albania', 'DZ': 'Algeria', 'AD': 'Andorra', 'AO': 'Angola',
    'AR': 'Argentina', 'AM': 'Armenia', 'AT': 'Austria', 'AZ': 'Azerbaijan',
    'BH': 'Bahrain', 'BD': 'Bangladesh', 'BY': 'Belarus', 'BE': 'Belgium',
    // Add more as needed
  };
  
  return countries[countryCode] || countryCode;
};

export default {
  getGeoLocation,
  submitLead,
  getCountryName
};
